import { useMemo, useState } from "react";
import { Avatar } from "@/components/Avatar";
import type { Conversation, PresenceStatus, UserBrief } from "@/messaging/types";
import { EmptyState } from "./EmptyState";
import { BellOffIcon, ChatIcon, PinIcon, PlusIcon, SearchIcon, UsersIcon } from "./icons";
import { shortAgo } from "./time";

/** The other participant of a direct chat (not me). */
export function counterpart(c: Conversation, myId: string | null): UserBrief | null {
  if (c.kind !== "direct") return null;
  return c.members.find((m) => m.user.id !== myId)?.user ?? c.members[0]?.user ?? null;
}

export function convTitle(c: Conversation, myId: string | null): string {
  if (c.kind === "group") return c.title || "Group";
  const other = counterpart(c, myId);
  return other?.full_name || other?.email || "Direct message";
}

function preview(c: Conversation): string {
  const lm = c.last_message;
  if (!lm) return "No messages yet";
  if (lm.deleted) return "Message deleted";
  if (lm.system_kind) return lm.body || "";
  const prefix = c.kind === "group" && lm.sender_name ? `${lm.sender_name.split(" ")[0]}: ` : "";
  if (lm.body) return prefix + lm.body;
  if (lm.has_attachment) return `${prefix}Attachment`;
  return prefix;
}

export function ConversationList({
  conversations,
  activeId,
  onSelect,
  onNewChat,
  presenceOf,
  myId,
  loading,
  error,
  onRetry,
}: {
  conversations: Conversation[];
  activeId: string | null;
  onSelect: (id: string) => void;
  onNewChat: () => void;
  presenceOf: (u: UserBrief | null | undefined) => PresenceStatus;
  myId: string | null;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
}) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return conversations;
    return conversations.filter((c) => convTitle(c, myId).toLowerCase().includes(t));
  }, [conversations, q, myId]);

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* search */}
      <div className="p-2.5">
        <div className="relative">
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">
            <SearchIcon size={16} />
          </span>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search chats"
            className="input w-full !pl-9 !py-2 !rounded-lg"
          />
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto scrollbar-thin">
        {loading && (
          <div className="space-y-1 px-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 px-2 py-2.5">
                <div className="h-9 w-9 rounded-full bg-white/[0.04] animate-pulse" />
                <div className="flex-1 space-y-1.5">
                  <div className="h-3 w-1/3 rounded bg-white/[0.04] animate-pulse" />
                  <div className="h-2.5 w-2/3 rounded bg-white/[0.03] animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && error && (
          <EmptyState
            title="Couldn't load chats"
            action={
              <button className="btn-secondary !py-1.5 !text-xs" onClick={onRetry}>
                Try again
              </button>
            }
          />
        )}

        {!loading && !error && filtered.length === 0 && (
          <EmptyState
            icon={<ChatIcon />}
            title={q ? "No matches" : "No chats yet"}
            hint={q ? "Try a different name." : "Start a conversation with a teammate."}
            action={
              !q ? (
                <button className="btn-primary !py-1.5 !text-xs" onClick={onNewChat}>
                  <PlusIcon size={15} /> New chat
                </button>
              ) : undefined
            }
          />
        )}

        {!loading &&
          !error &&
          filtered.map((c) => {
            const other = counterpart(c, myId);
            const title = convTitle(c, myId);
            const active = c.id === activeId;
            const unread = !c.muted && c.unread_count > 0;
            return (
              <button
                key={c.id}
                onClick={() => onSelect(c.id)}
                className={`flex w-full items-center gap-3 px-3 py-2.5 text-left transition ${
                  active ? "bg-accent-500/10" : "hover:bg-white/[0.03]"
                }`}
              >
                {c.kind === "group" ? (
                  <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-accent-500/15 text-accent-300">
                    <UsersIcon size={18} />
                  </span>
                ) : (
                  <Avatar
                    url={other?.avatar_url}
                    name={other?.full_name}
                    email={other?.email}
                    size="sm"
                    status={presenceOf(other)}
                  />
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span
                      className={`truncate text-sm ${
                        unread ? "font-semibold text-slate-100" : "font-medium text-slate-200"
                      }`}
                    >
                      {title}
                    </span>
                    {c.pinned && <PinIcon size={12} className="text-slate-500 flex-shrink-0" />}
                    {c.muted && <BellOffIcon size={12} className="text-slate-600 flex-shrink-0" />}
                    <span className="ml-auto flex-shrink-0 text-[10.5px] text-slate-500">
                      {shortAgo(c.last_message?.created_at ?? c.created_at)}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span
                      className={`truncate text-[12.5px] ${
                        unread ? "text-slate-300" : "text-slate-500"
                      }`}
                    >
                      {preview(c)}
                    </span>
                    {unread && (
                      <span className="ml-auto flex h-4 min-w-4 flex-shrink-0 items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold text-white tabular-nums">
                        {c.unread_count > 99 ? "99+" : c.unread_count}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
      </div>
    </div>
  );
}
